import { API_BASE_URL } from "./utils";

export const TICKET_CATEGORIES = [
  { key: "internal", label: "Internal", hint: "Customer tickets from dietitians" },
  { key: "sales", label: "Sales", hint: "Cash, bank, hire, customer payments" },
  { key: "support", label: "Support", hint: "Dietitian ↔ Super Admin" },
];

export const CATEGORY_STYLES = {
  internal:
    "bg-violet-50 text-violet-800 border-violet-200 dark:bg-violet-950/50 dark:text-violet-200 dark:border-violet-800",
  sales:
    "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800",
  support:
    "bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-200 dark:border-sky-800",
};

export const PRIORITY_MAP = {
  high: {
    label: "Urgent",
    color: "text-rose-700 dark:text-rose-300",
    bg: "bg-rose-50 dark:bg-rose-950/50",
    border: "border-rose-200 dark:border-rose-900",
    accent: "bg-rose-500",
    dot: "bg-rose-500",
  },
  medium: {
    label: "Normal",
    color: "text-amber-700 dark:text-amber-300",
    bg: "bg-amber-50 dark:bg-amber-950/40",
    border: "border-amber-200 dark:border-amber-900",
    accent: "bg-amber-500",
    dot: "bg-amber-400",
  },
  low: {
    label: "Low",
    color: "text-teal-700 dark:text-teal-300",
    bg: "bg-teal-50 dark:bg-teal-950/40",
    border: "border-teal-200 dark:border-teal-900",
    accent: "bg-teal-500",
    dot: "bg-teal-500",
  },
};

export const CATEGORY_LABELS = {
  internal: "Internal",
  sales: "Sales",
  support: "Support",
};

export const PLAN_FILTER_OPTIONS = [
  { value: "", label: "All plans" },
  { value: "premium", label: "Premium" },
  { value: "ai_plan", label: "AI Plan" },
  { value: "trial", label: "Trial" },
  { value: "unpaid", label: "Unpaid / no plan" },
  { value: "premium_expired", label: "Premium expired" },
  { value: "trial_expired", label: "Trial expired" },
];

export const SALES_REQUEST_TYPES = [
  { key: "hire_pa", label: "Hire Personal Assistant" },
  { key: "hire_dietitian", label: "Hire Dietitian" },
  { key: "hire_pa_terminate", label: "Terminate Personal Assistant" },
  { key: "hire_dietitian_terminate", label: "Terminate Personal Dietitian" },
  { key: "cash", label: "Cash payment" },
  { key: "bank_transfer", label: "Bank transfer" },
];

export const WALLET_TOPUP_TYPES = [
  { key: "cash", label: "Cash payment" },
  { key: "bank_transfer", label: "Bank transfer" },
];

export const CUSTOMER_WALLET_TOPUP_TYPES = [
  { key: "customer_cash", label: "Customer cash payment" },
  { key: "customer_bank_transfer", label: "Customer bank transfer" },
];

export const SALES_PLAN_TYPES = [
  { key: "premium", label: "Premium plan purchased" },
  { key: "ai_plan", label: "AI Plan purchased" },
  { key: "pay_as_you_go", label: "Pay as You Go" },
  { key: "subscription", label: "Subscription" },
  { key: "per_diet_plan", label: "Per Diet Plan" },
  { key: "appointment_chat", label: "Appointment by Chat" },
  { key: "appointment_call", label: "Appointment by Call" },
];

export const SALES_SUBTYPES = [
  ...SALES_REQUEST_TYPES,
  ...SALES_PLAN_TYPES,
  ...CUSTOMER_WALLET_TOPUP_TYPES,
];

export function salesRequestLabel(requestType) {
  return (
    SALES_SUBTYPES.find((r) => r.key === requestType)?.label || ""
  );
}

/** Hire / terminate-hire tickets that change assigned PA or dietitian status. */
export function isHireStaffTicket(ticketOrType) {
  const t = String(
    typeof ticketOrType === "string"
      ? ticketOrType
      : ticketOrType?.request_type || "",
  ).toLowerCase();
  return (
    t === "hire_pa" ||
    t === "hire_dietitian" ||
    t === "hire_pa_terminate" ||
    t === "hire_dietitian_terminate"
  );
}

export function formatTicketNumber(ticketId) {
  if (ticketId == null || ticketId === "") return "—";
  return `#${ticketId}`;
}

export function formatTicketDate(dateVal) {
  if (!dateVal) return "—";
  const d = new Date(dateVal);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatTicketDateTime(dateVal) {
  if (!dateVal) return "—";
  const d = new Date(dateVal);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatTicketTimeShort(dateVal) {
  if (!dateVal) return "";
  const d = new Date(dateVal);
  if (Number.isNaN(d.getTime())) return "";
  const sameDay = new Date().toDateString() === d.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  }
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function ticketDayKey(dateVal) {
  if (!dateVal) return "";
  const d = new Date(dateVal);
  if (Number.isNaN(d.getTime())) return "";
  return d.toDateString();
}

export function ticketDayLabel(dateVal) {
  const key = ticketDayKey(dateVal);
  if (!key) return "";
  const today = new Date().toDateString();
  if (key === today) return "Today";
  const yest = new Date();
  yest.setDate(yest.getDate() - 1);
  if (key === yest.toDateString()) return "Yesterday";
  return new Date(dateVal).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export function ticketDisplayName(nameOrEmail) {
  const raw = String(nameOrEmail || "").trim();
  if (!raw) return "";
  if (!raw.includes("@")) return raw;
  const local = raw.split("@")[0];
  return local.replace(/[._]/g, " ");
}

export function formatTicketRelative(dateVal) {
  if (!dateVal) return "—";
  const d = new Date(dateVal);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return formatTicketDate(dateVal);
}

export function isWalletTopupTicket(ticket) {
  const t = String(ticket?.request_type || "").toLowerCase();
  return t === "cash" || t === "bank_transfer";
}

export function isCustomerWalletTopupTicket(ticket) {
  const t = String(ticket?.request_type || "").toLowerCase();
  return t === "customer_cash" || t === "customer_bank_transfer";
}

export function isPaymentTicket(ticket) {
  return isWalletTopupTicket(ticket) || isCustomerWalletTopupTicket(ticket);
}

const DESCRIPTION_FIELD_LABELS = {
  amount: "Amount",
  txn: "Transaction",
  note: "Note",
  method: "Method",
  plan: "Plan",
  customer: "Customer",
};

/** Split ticket.description into a summary + labeled fields (Amount, txn, Note, …). */
export function parseTicketDescription(ticket) {
  const raw = String(ticket?.description || "").trim();
  if (!raw) return null;

  const lines = raw.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  const fields = [];
  const summaryLines = [];
  const seen = new Set();

  const pushField = (key, value) => {
    const v = String(value || "").trim();
    if (!v || seen.has(key)) return;
    seen.add(key);
    fields.push({
      key,
      label: DESCRIPTION_FIELD_LABELS[key] || key,
      value: v,
    });
  };

  for (const line of lines) {
    const labeled = line.match(
      /^(Amount|Note|Method|Plan|Customer|Transaction|Txn)\s*:\s*(.+)$/i,
    );
    if (labeled) {
      const rawKey = labeled[1].toLowerCase();
      const key = rawKey === "transaction" || rawKey === "txn" ? "txn" : rawKey;
      pushField(key, labeled[2]);
      continue;
    }
    summaryLines.push(line);
  }

  if (ticket?.related_transaction_id) {
    pushField("txn", ticket.related_transaction_id);
  }

  const summary = summaryLines.join(" ").trim();
  if (!summary && !fields.length) return null;
  return { summary, fields, raw };
}

export function ticketListedAmount(ticket) {
  const parsed = parseTicketDescription(ticket);
  const fromField = parsed?.fields?.find((f) => f.key === "amount")?.value;
  if (fromField) {
    const match = String(fromField).match(/([0-9][0-9,]*(?:\.[0-9]+)?)/);
    if (!match) return null;
    const n = Number(String(match[1]).replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
  }
  const desc = String(ticket?.description || "");
  const match = desc.match(/Amount:\s*(?:\$|Rs\s+|[A-Z]{3}\s+)?([0-9][0-9,]*(?:\.[0-9]+)?)/i);
  if (!match) return null;
  const n = Number(String(match[1]).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

export function formatTicketUsd(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "";
  return `$${n.toFixed(2)}`;
}

/** Prefer the ticket's original Amount text so dietitian-currency charges stay as written. */
export function ticketListedAmountLabel(ticket) {
  const parsed = parseTicketDescription(ticket);
  const fromField = parsed?.fields?.find((f) => f.key === "amount")?.value;
  if (fromField) return String(fromField).trim();
  const n = ticketListedAmount(ticket);
  if (n == null) return "";
  return formatTicketUsd(n);
}

export function isCreateDietPlanTicket(ticket) {
  const type = String(ticket?.request_type || "").toLowerCase();
  return (
    type === "per_diet_plan" ||
    type === "subscription" ||
    type === "pay_as_you_go"
  );
}

export function ticketCategoryOf(ticket) {
  const requestType = String(ticket?.request_type || "").toLowerCase();
  if (requestType === "pay_as_you_go") return "sales";
  if (
    requestType === "per_diet_plan" ||
    requestType === "subscription" ||
    requestType === "appointment_chat" ||
    requestType === "appointment_call" ||
    requestType === "customer_support"
  ) {
    return "internal";
  }
  const cat = String(ticket?.category || "").toLowerCase();
  if (cat === "internal" || cat === "sales" || cat === "support") return cat;
  if (
    requestType === "hire_pa" ||
    requestType === "hire_dietitian" ||
    requestType === "cash" ||
    requestType === "bank_transfer" ||
    requestType === "customer_cash" ||
    requestType === "customer_bank_transfer" ||
    requestType === "premium" ||
    requestType === "ai_plan" ||
    requestType === "pay_as_you_go"
  ) {
    return "sales";
  }
  if (ticket?.customer_ref) return "internal";
  return "support";
}

export function ticketNoteRoleOf(note) {
  const role = String(note?.added_by_role || "").toLowerCase();
  if (role === "superadmin") return "superadmin";
  if (role === "customer") return "customer";
  return "dietitian";
}

export function ticketNoteRoleLabel(note) {
  const role = ticketNoteRoleOf(note);
  if (role === "superadmin") return "Super Admin";
  if (role === "customer") return "Customer";
  return "Dietitian";
}

export function ticketAttachmentUrl(path) {
  if (!path) return "";
  const s = String(path);
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  const origin = API_BASE_URL.replace(/\/api\/v1\/?$/, "");
  if (s.startsWith("/")) return `${origin}${s}`;
  return `${origin}/api/uploads/${s}`;
}

export function ticketAttachmentIsImage(att) {
  const mime = String(att?.mime || "");
  const name = String(att?.name || att?.url || "");
  return mime.startsWith("image/") || /\.(jpe?g|png|gif|webp|heic)$/i.test(name);
}

export function ticketInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "WD";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

/** True when the last conversation turn belongs to the other party. */
export function ticketNeedsReply(ticket, viewer) {
  if (String(ticket?.status || "") !== "open") return false;
  const notes = Array.isArray(ticket?.notes) ? ticket.notes : [];
  if (!notes.length) return viewer === "superadmin";
  const lastRole = ticketNoteRoleOf(notes[notes.length - 1]);
  if (viewer === "superadmin") return lastRole !== "superadmin";
  return lastRole === "superadmin" || lastRole === "customer";
}

export function mergeRealtimeTicket(prev, incoming, opts = {}) {
  if (!incoming?._id) return prev;
  const id = String(incoming._id);
  const statusFilter = opts.status || "all";
  const belongs = typeof opts.belongs === "function" ? opts.belongs : () => true;
  const statusOk = statusFilter === "all" || String(incoming.status) === statusFilter;
  const keep = belongs(incoming) && statusOk;
  const without = (prev || []).filter((t) => String(t._id) !== id);
  if (!keep) return without;
  return [incoming, ...without];
}
